import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react'
import { useOrchestration } from '../hooks/useOrchestration'
import { useObserver } from '../hooks/useObserver'
import { useWorkspacePersistence } from '../hooks/useWorkspacePersistence'
import type { UseOrchestrationReturn, GroupEvent } from '../hooks/useOrchestration'
import type { UseObserverReturn } from '../hooks/useObserver'
import type { UseCanvasReturn } from '../hooks/useCanvas'
import type { UsePatientPanelReturn } from '../hooks/usePatientPanel'
import type { AgentToolCall } from './ChatContext'
import type {
  WorkspaceSummary,
  WorkspaceType,
  WorkspaceUpdate,
  HydrationResponse,
} from '../services/workspaceService'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

interface WorkspaceState {
  /** Server-backed workspace summaries (sidebar list) */
  workspaces: WorkspaceSummary[]
  /** Currently active workspace UUID */
  activeWorkspaceId: string | null
  /** Whether the canvas/artifact panel is open */
  isCanvasOpen: boolean
  /** Group events for round table display */
  groupEvents: GroupEvent[]
  /** True while initial workspace list is loading */
  isLoading: boolean
  /** True while a workspace hydration is in flight */
  isHydrating: boolean
  /** Error message from the last failed operation */
  error: string | null
  /** Full hydration payload for the active workspace */
  hydrationData: HydrationResponse | null
  /** Workspace type pre-selected via sidebar dropdown (null when no pending selection) */
  pendingWorkspaceType: WorkspaceType | null
}

interface WorkspaceActions {
  /** Create a new server-backed workspace */
  createWorkspace: (type: WorkspaceType, name?: string) => Promise<string>
  /** Switch to a workspace (triggers hydration) */
  switchWorkspace: (id: string) => Promise<void>
  /** Close (soft) or delete (hard) a workspace */
  closeWorkspace: (id: string, hard?: boolean) => Promise<void>
  /** Reopen a previously closed workspace */
  reopenWorkspace: (id: string) => Promise<void>
  /** Debounced save of workspace state updates */
  saveState: (updates: Partial<WorkspaceUpdate>) => void
  /** Refresh workspace list from server */
  refreshList: (includeClosed?: boolean) => Promise<void>
  /** Re-hydrate the active workspace (re-fetches artifacts, conversations, clinical data) */
  rehydrate: (id: string) => Promise<void>
  /** Refresh just clinical data for a workspace (merges into existing hydrationData) */
  refreshClinicalData: (id: string) => Promise<void>
  /** Refresh a single artifact by ID (merges into existing hydrationData.artifacts) */
  refreshArtifact: (id: string, artifactId: string) => Promise<void>
  /** Toggle canvas open state */
  setCanvasOpen: (open: boolean) => void
  /** Add a group event (round table) */
  addGroupEvent: (event: GroupEvent) => void
  /** Clear all group events */
  clearGroupEvents: () => void
  /** Navigate to IntakePage with a pre-selected workspace type (saves current state first) */
  navigateToIntake: (type: WorkspaceType) => Promise<void>
}

/** Orchestration methods exposed through workspace context */
interface OrchestrationActions {
  orchestration: UseOrchestrationReturn | null
  observer: UseObserverReturn | null
}

/** Props for WorkspaceProvider — hooks are wired from the parent */
interface WorkspaceProviderProps {
  children: React.ReactNode
  canvas?: UseCanvasReturn
  panel?: UsePatientPanelReturn
  sendToAgent?: (agentId: string, message: string, mode?: string, canvasSnapshots?: Record<string, string>, options?: { hidden?: boolean; orchestrate?: boolean }) => Promise<{ output: string; tools: AgentToolCall[] } | null>
}

type WorkspaceContextValue = WorkspaceState & WorkspaceActions & OrchestrationActions

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined)

// ────────────────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────────────────

export function WorkspaceProvider({
  children,
  canvas,
  panel,
  sendToAgent: sendToAgentProp,
}: WorkspaceProviderProps) {
  // Server-backed workspace persistence
  const persistence = useWorkspacePersistence()

  const [pendingWorkspaceType, setPendingWorkspaceType] = useState<WorkspaceType | null>(null)

  // Local UI state (not persisted per-workspace — ephemeral for current session)
  const [isCanvasOpen, setIsCanvasOpen] = useState(false)
  const [groupEvents, setGroupEvents] = useState<GroupEvent[]>([])

  const pipelineRunningRef = useRef(false)

  // ── Canvas toggle ──

  const setCanvasOpenCb = useCallback((open: boolean) => {
    setIsCanvasOpen(open)
  }, [])

  // ── Group Events ──

  const addGroupEvent = useCallback((event: GroupEvent) => {
    setGroupEvents(prev => [...prev, event])
  }, [])

  const clearGroupEvents = useCallback(() => {
    setGroupEvents([])
  }, [])

  // ── Navigate to intake ──

  const navigateToIntake = useCallback(async (type: WorkspaceType) => {
    await persistence.deselectWorkspace()
    setPendingWorkspaceType(type)
  }, [persistence.deselectWorkspace])

  // Wrap create/switch to clear pending type
  const createWorkspaceWrapped = useCallback(async (type: WorkspaceType, name?: string) => {
    const id = await persistence.createWorkspace(type, name)
    setPendingWorkspaceType(null)
    return id
  }, [persistence.createWorkspace])

  const switchWorkspaceWrapped = useCallback(async (id: string) => {
    await persistence.switchWorkspace(id)
    setPendingWorkspaceType(null)
  }, [persistence.switchWorkspace])

  // ── Observer (wired only when dependencies are available) ──

  const observerResult = useObserver(
    canvas && sendToAgentProp
      ? {
          workspaceId: persistence.activeWorkspaceId,
          canvas,
          sendToAgent: sendToAgentProp,
          addGroupEvent,
        }
      : {
          workspaceId: null,
          canvas: null as unknown as UseCanvasReturn,
          sendToAgent: async () => null,
          addGroupEvent,
        }
  )

  // ── Orchestration (wired only when dependencies are available) ──

  const orchestrationResult = useOrchestration(
    canvas && panel && sendToAgentProp
      ? {
          workspaceId: persistence.activeWorkspaceId,
          canvas,
          panel,
          sendToAgent: sendToAgentProp,
          addGroupEvent,
          observer: observerResult,
          pipelineRunningRef,
          setCanvasOpen: setCanvasOpenCb,
        }
      : {
          workspaceId: null,
          canvas: null as unknown as UseCanvasReturn,
          panel: null as unknown as UsePatientPanelReturn,
          sendToAgent: async () => null,
          addGroupEvent,
          pipelineRunningRef,
          setCanvasOpen: setCanvasOpenCb,
        }
  )

  // Only expose orchestration when deps are available
  const hasOrchestration = !!(canvas && panel && sendToAgentProp)

  const value = useMemo<WorkspaceContextValue>(() => ({
    // State — server-backed
    workspaces: persistence.workspaces,
    activeWorkspaceId: persistence.activeWorkspaceId,
    isLoading: persistence.isLoading,
    isHydrating: persistence.isHydrating,
    error: persistence.error,
    hydrationData: persistence.hydrationData,

    // State — local/ephemeral
    isCanvasOpen,
    groupEvents,
    pendingWorkspaceType,

    // Workspace actions — server-backed
    createWorkspace: createWorkspaceWrapped,
    switchWorkspace: switchWorkspaceWrapped,
    closeWorkspace: persistence.closeWorkspace,
    reopenWorkspace: persistence.reopenWorkspace,
    saveState: persistence.saveState,
    refreshList: persistence.refreshList,
    rehydrate: persistence.rehydrate,
    refreshClinicalData: persistence.refreshClinicalData,
    refreshArtifact: persistence.refreshArtifact,

    // Local UI actions
    setCanvasOpen: setCanvasOpenCb,
    addGroupEvent,
    clearGroupEvents,
    navigateToIntake,

    // Orchestration
    orchestration: hasOrchestration ? orchestrationResult : null,
    observer: hasOrchestration ? observerResult : null,
  }), [
    persistence.workspaces,
    persistence.activeWorkspaceId,
    persistence.isLoading,
    persistence.isHydrating,
    persistence.error,
    persistence.hydrationData,
    createWorkspaceWrapped,
    switchWorkspaceWrapped,
    persistence.closeWorkspace,
    persistence.reopenWorkspace,
    persistence.saveState,
    persistence.refreshList,
    persistence.rehydrate,
    persistence.refreshClinicalData,
    persistence.refreshArtifact,
    isCanvasOpen,
    groupEvents,
    pendingWorkspaceType,
    setCanvasOpenCb,
    addGroupEvent,
    clearGroupEvents,
    navigateToIntake,
    hasOrchestration,
    orchestrationResult,
    observerResult,
  ])

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  )
}

// ────────────────────────────────────────────────────────
// Consumer hooks
// ────────────────────────────────────────────────────────

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider')
  }
  return context
}
